import React, { useMemo, useState } from 'react';
import '../crm.css';

const statusLabels = {
    available: 'Available',
    hold: 'Hold',
    booked: 'Booked',
    sold: 'Sold',
    inactive: 'Inactive',
};

const typeLabels = {
    plot: 'Plot',
    flat: 'Flat',
    villa: 'Villa',
    commercial: 'Commercial',
    farm: 'Farm',
    other: 'Other',
};

const formatCurrency = (value) => {
    if (value === undefined || value === null || value === '') return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(Number(value) || 0);
};

const formatArea = (area, unit) => {
    if (area === undefined || area === null || area === '') return '-';
    return `${area} ${unit || 'sqft'}`;
};

function PropertyMatchCard({ match, onSchedule, onOpen, onCreateDeal, onShare }) {
    const property = useMemo(() => {
        const base = match?.propertySnapshot || match?.propertyPreview || match?.property || {};
        const id = base.id || match?.propertyId;
        return { ...base, id };
    }, [match]);
    const score = Math.round(match?.score || 0);
    const reasons = Array.isArray(match?.reasons) ? match.reasons : [];
    const [showAllReasons, setShowAllReasons] = useState(false);
    const status = property.status || 'available';
    const propertyType = property.propertyType || 'other';
    const locationParts = [property.locality, property.city].filter(Boolean).join(', ') || '';
    const pincode = property.pincode || match?.pincode;
    const locationLabel = pincode
        ? (locationParts ? `${locationParts} • ${pincode}` : pincode)
        : (locationParts || '-');
    const listingIntent = property.listingIntent || property.listingType || 'unknown';
    const listingLabel = listingIntent === 'rent' ? 'For Rent' : listingIntent === 'sale' ? 'For Sale' : 'Intent Unknown';
    const scoreStyle = score >= 80 ? 'high' : score >= 50 ? 'mid' : 'low';
    const reasonLabels = reasons.map((reason) => (typeof reason === 'string' ? reason : reason.label)).filter(Boolean);
    const visibleReasons = showAllReasons ? reasonLabels : reasonLabels.slice(0, 3);
    const sourceLeadName = property?.meta?.source === 'lead' ? property?.meta?.sourceLeadName : null;

    return (
        <div className="crm-card crm-match-card">
            <div className="crm-card-body">
                <div className="crm-match-header">
                    <div className="crm-match-score">
                        <div className={`crm-match-score-value ${scoreStyle}`}>{score}</div>
                        <div className="crm-match-score-label">Match</div>
                    </div>
                    <div className="crm-match-summary">
                        <div className="crm-match-title">{property.title || 'Untitled Property'}</div>
                        <div className="crm-match-subtitle">{locationLabel}</div>
                        <div className="crm-match-badges">
                            <span className={`crm-badge crm-badge-${status}`}>{statusLabels[status] || status}</span>
                            <span className="crm-badge crm-badge-source">{typeLabels[propertyType] || 'Other'}</span>
                            {pincode && <span className="crm-badge" style={{ background: '#111', color: '#fff' }}>{pincode}</span>}
                            <span className="crm-badge crm-badge-source">{listingLabel}</span>
                            {sourceLeadName && <span className="crm-badge crm-badge-source">From Lead: {sourceLeadName}</span>}
                        </div>
                    </div>
                </div>

                <div className="crm-match-bar">
                    <span style={{ width: `${Math.min(score, 100)}%` }} />
                </div>

                <div className="crm-match-meta">
                    <div>
                        <div className="crm-meta-label">Price</div>
                        <div className="crm-meta-value">{formatCurrency(property.price)}</div>
                    </div>
                    <div>
                        <div className="crm-meta-label">Area</div>
                        <div className="crm-meta-value">{formatArea(property.area, property.areaUnit)}</div>
                    </div>
                    <div>
                        <div className="crm-meta-label">BHK</div>
                        <div className="crm-meta-value">{property.bhk ?? property.bedrooms ?? '-'}</div>
                    </div>
                </div>

                <div className="crm-match-reasons">
                    {visibleReasons.length > 0 ? visibleReasons.map((reason) => (
                        <span key={reason} className="crm-reason-chip">{reason}</span>
                    )) : <span className="crm-reason-chip">No strong signals</span>}
                    {reasonLabels.length > 3 && (
                        <button
                            type="button"
                            className="crm-reason-chip crm-reason-chip-action"
                            onClick={() => setShowAllReasons((prev) => !prev)}
                        >
                            {showAllReasons ? 'Show less' : `+${reasonLabels.length - 3} more`}
                        </button>
                    )}
                </div>

                <div className="crm-match-actions">
                    <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => onOpen?.(property)} disabled={!onOpen}>
                        Open Property
                    </button>
                    <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => onShare?.(property)}>
                        Share WhatsApp
                    </button>
                    <button className="crm-btn crm-btn-primary crm-btn-sm" type="button" onClick={() => onSchedule?.(property)}>
                        Schedule Visit
                    </button>
                    <button className="crm-btn crm-btn-primary crm-btn-sm" type="button" onClick={() => onCreateDeal?.(property)}>
                        Create Deal
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PropertyMatchCard;
